import { FeatureCollection, LineString, Point } from "@turf/helpers";
import { CommonRequestParameters } from "api/lokiRequestModels/CommonRequestParameters";
import { LaneAggregationsRequestModel } from "api/lokiRequestModels/lane/LaneAggregationsRequestModel";
import { LanesDetailsRequestModel } from "api/lokiRequestModels/lane/LanesDetailsRequestModel";
import { TempExcursionAggregationRequestModel } from "api/lokiRequestModels/lane/TempExcursionAggregationRequestModel";
import { ShipmentDetailsRequestModel } from "api/lokiRequestModels/shipments/ShipmentDetailsRequestModel";
import { services } from "api/serviceConfig";
import { dateEndFormatting, dateStartFormatting } from "helpers/dateFormatting";
import { convertLanesToGeojson, convertLanesToPoints, convertLaneToStraightShortestPath } from "helpers/mapHelpers";
import { makeAutoObservable } from "mobx";
import { LaneAggregations } from "models/laneAggregations/LaneAggregations";
import { LaneDetails } from "models/laneDetails/LaneDetails";
import { ProductExcursions } from "models/laneExcursion/ProductExcursions";
import { LaneMapDetails } from "models/laneMapDetails/LaneMapDetails";
import { ShipmentDetails } from "./../models/shipmentDetails/ShipmentDetails";
import FiltersStore from "./FiltersStore";
import IdentityStore from "./IdentityStore";

export class GlobalViewStore {
    identityStore: IdentityStore;
    filtersStore: FiltersStore;

    aggregations: LaneAggregations[] = [];
    temperatureExcursions: ProductExcursions[] = [];

    lanePages: LaneDetails[][] = [];
    mapDetails: LaneMapDetails[] = [];
    currentLanesPage = 1;
    totalLanesLoadedPages = 1;
    lastLanePage = 0;
    laneNextToken: string | undefined = undefined;

    shipmentPages: ShipmentDetails[][] = [];
    currentShipmentPage = 1;
    totalShipmentsLoadedPages = 1;
    lastShipmentPage = 0;
    shipmentNextToken: string | undefined = undefined;

    selectedLane: Partial<LaneDetails> | null = null;

    loadingAggregations = false;
    loadingTemperatureExcursions = false;
    loadingLanes = false;
    loadingMap = false;
    loadingShipments = false;

    laneSort: string = "riskLevel";
    laneDirection: "ASC" | "DESC" = "DESC";

    shipmentSort: string = "riskLevel";
    shipmentDirection: "ASC" | "DESC" = "DESC";

    get isLastLanePage() {
        return this.currentLanesPage === this.lastLanePage;
    }

    get isLastShipmentPage() {
        return this.currentShipmentPage === this.lastShipmentPage;
    }

    get pointsGeojson(): FeatureCollection<Point> {
        if (this.mapDetails.length > 0) {
            return convertLanesToPoints(this.mapDetails);
        }

        return { type: "FeatureCollection", features: [] };
    }

    get lanesGeojson(): FeatureCollection<LineString> {
        if (this.mapDetails.length > 0) {
            return convertLanesToGeojson(this.mapDetails);
        }

        return { type: "FeatureCollection", features: [] };
    }

    get selectedLaneLineGeojson(): FeatureCollection<LineString> {
        if (this.selectedLane) {
            return convertLaneToStraightShortestPath(this.selectedLane as LaneDetails);
        }

        return { type: "FeatureCollection", features: [] };
    }

    get selectedLanePointsGeojson(): FeatureCollection<Point> {
        if (this.selectedLane) {
            return convertLanesToPoints([this.selectedLane as LaneDetails]);
        }

        return { type: "FeatureCollection", features: [] };
    }

    get selectedLaneName() {
        if (this.selectedLane) {
            return this.selectedLane.laneName;
        }

        return "";
    }

    constructor(filtersStore: FiltersStore, identityStore: IdentityStore) {
        makeAutoObservable(this);
        this.filtersStore = filtersStore;
        this.identityStore = identityStore;
    }

    setLaneSortProperty = (property: string) => {
        this.laneSort = property;
    };

    setLaneDirection = (direction: "ASC" | "DESC") => {
        this.laneDirection = direction;
    };

    setShipmentSortProperty = (property: string) => {
        this.shipmentSort = property;
    };

    setShipmentDirection = (direction: "ASC" | "DESC") => {
        this.shipmentDirection = direction;
    };

    loadMapDetails = async (request: CommonRequestParameters) => {
        this.loadingMap = true;
        try {
            const response = await services.Lane.getListMapDetails(request);
            this.mapDetails = response.data.laneMapDetails || [];
        } catch (error) {
            console.log(error);
        } finally {
            this.loadingMap = false;
        }
    };

    setSelectedLane = async (id: string) => {
        for (let i = 0; i < this.lanePages.length; i++) {
            for (let j = 0; j < this.lanePages[i].length; j++) {
                if (this.lanePages[i][j].laneId === id) {
                    this.selectedLane = this.lanePages[i][j];
                    return;
                }
            }
        }

        const allLanesRequest: CommonRequestParameters = {
            customerId: this.identityStore.currentCustomerId,
            startTime: dateStartFormatting(this.filtersStore.startDate),
            endTime: dateEndFormatting(this.filtersStore.endDate),
        };

        try {
            const response = await services.Lane.getListMapDetails(allLanesRequest);
            const lane = response.data.laneMapDetails.find((x) => x.laneId === id);

            if (lane) {
                this.selectedLane = lane;
            }
        } catch (error) {
            console.log(error);
        }
    };

    loadAggregations = async (request: LaneAggregationsRequestModel) => {
        this.loadingAggregations = true;

        try {
            const response = await services.Lane.getLaneAggregations(request);
            this.aggregations = response.data.laneAggregations || [];
        } catch (error) {
            console.log(error);
        } finally {
            this.loadingAggregations = false;
        }
    };

    loadTemperatureExcursions = async (request: TempExcursionAggregationRequestModel) => {
        this.loadingTemperatureExcursions = true;

        try {
            const response = await services.Lane.getTempExcursionAggregation(request);
            this.temperatureExcursions = response.data.tempExcursionAggregations || [];
        } catch (error) {
            console.log(error);
        } finally {
            this.loadingTemperatureExcursions = false;
        }
    };

    loadFirstShipmentPage = async (request: ShipmentDetailsRequestModel) => {
        this.loadingShipments = true;
        try {
            const response = await services.Shipment.allShipmentsDetails(request);
            this.clearShipmentsPagination();
            if (response.data.shipmentDetails.length > 0) {
                this.shipmentPages.push(response.data.shipmentDetails);
            }
            if (!response.data.nextToken) {
                this.lastShipmentPage = this.currentShipmentPage;
            } else {
                this.shipmentNextToken = response.data.nextToken;
            }
        } catch (error) {
            this.lastShipmentPage = this.currentShipmentPage;
            console.log(error);
        } finally {
            this.loadingShipments = false;
        }
    };

    moveToNextShipmentPage = async (request: ShipmentDetailsRequestModel) => {
        this.loadingShipments = true;
        try {
            if (this.totalShipmentsLoadedPages === this.currentShipmentPage) {
                const response = await services.Shipment.allShipmentsDetails(request);
                this.shipmentPages.push(response.data.shipmentDetails);
                if (!response.data.nextToken) {
                    this.lastShipmentPage = this.currentShipmentPage + 1;
                } else {
                    this.shipmentNextToken = response.data.nextToken;
                    this.totalShipmentsLoadedPages++;
                }
            }
            this.currentShipmentPage++;
        } catch (error) {
            console.log(error);
        } finally {
            this.loadingShipments = false;
        }
    };

    moveToPreviousShipmentPage = () => {
        this.currentShipmentPage--;
    };

    loadFirstLanePage = async (request: LanesDetailsRequestModel) => {
        this.loadingLanes = true;

        try {
            const response = await services.Lane.listLanesDetails(request);
            this.clearLanePagination();
            if (response.data.laneDetails.length > 0) {
                this.lanePages.push(response.data.laneDetails);
            }
            if (!response.data.nextToken) {
                this.lastLanePage = this.currentLanesPage;
            } else {
                this.laneNextToken = response.data.nextToken;
            }
        } catch (error) {
            this.lastLanePage = this.currentLanesPage;
            console.log(error);
        } finally {
            this.loadingLanes = false;
        }
    };

    moveToNextLanePage = async (request: LanesDetailsRequestModel) => {
        this.loadingLanes = true;

        try {
            if (this.totalLanesLoadedPages === this.currentLanesPage) {
                const response = await services.Lane.listLanesDetails(request);
                this.lanePages.push(response.data.laneDetails);
                if (!response.data.nextToken) {
                    this.lastLanePage = this.currentLanesPage + 1;
                } else {
                    this.laneNextToken = response.data.nextToken;
                    this.totalLanesLoadedPages++;
                }
            }
            this.currentLanesPage++;
        } catch (error) {
            console.log(error);
        } finally {
            this.loadingLanes = false;
        }
    };

    moveToPreviousLanePage = () => {
        this.currentLanesPage--;
    };

    clearLanePagination = () => {
        this.lanePages = [];
        this.currentLanesPage = 1;
        this.totalLanesLoadedPages = 1;
        this.lastLanePage = 0;
        this.laneNextToken = undefined;
    };

    clearShipmentsPagination = () => {
        this.shipmentPages = [];
        this.currentShipmentPage = 1;
        this.totalShipmentsLoadedPages = 1;
        this.lastShipmentPage = 0;
        this.shipmentNextToken = undefined;
    };

    resetShipmentFilters = () => {
        this.shipmentSort = "riskLevel";
        this.shipmentDirection = "DESC";
    };

    resetLaneFilters = () => {
        this.laneSort = "riskLevel";
        this.laneDirection = "DESC";
    };
}
